//#region imports
import { Component, ViewChild, HostListener, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { Subscription } from 'rxjs/Subscription';
import { filter, startWith, take } from 'rxjs/operators';

import { FinanceActions } from '@toro/common/shared/state-management/finance/finance.action';
import { StocksMarketAction } from '@toro/common/shared/state-management/stocks-market/stocks-market.action';
import { StocksExchangeWalletAction } from '@toro/common/shared/state-management/stocks-exchange-wallet/stocks-exchange-wallet.action';
import { UserProfileActions } from '@toro/common/shared/state-management/userprofile/userprofile.actions';
import { UtilsAction } from '@toro/common/shared/state-management/utils/utils.actions';
import { UsersAction } from '@toro/common/shared/state-management/users/users.actions';
import { ResearchActions } from '@toro/common/shared/state-management/research/research.action';
import { ExchangeActions } from '../../../state-management/exchange/exchange.action';
import { SuitabilityBypassAgreementActions } from '../../../state-management/suitability-bypass-agreement/suitability-bypass-agreement.action';
import { UsersEffects } from '@toro/common/shared/state-management/users/users.effects';

import { Roles } from '@toro/common/shared/models/roles/roles.enum';
import { KeyStocks } from '@toro/common/shared/enum/key-stocks/key-stocks.enum';
import { RiskModel } from '@toro/common/shared/enum/risk-model.enum';
import { BrokerageModel } from '@toro/common/shared/enum/billing/brokerage-model.enum';
import { LeverageLevel } from '../../../enum/leverageLevel.enum';

import { SettingsService } from '@toro/common/shared/services/settings/settings.service';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { DateFormatService } from '@toro/common/shared/services/date-format/date-format.service';
import { AppcuesOnboardingObserver } from '@toro/common/shared/services/appcues/ng/observer.service';
import { UserReRegistationService } from '@toro/common/shared/services/re-registration/user-re-registration.service';
import { UtilitiesService } from '@toro/common/shared/services/utilities.service';
import { IReRegistrationMessage } from '@toro/common/shared/interfaces/re-registration/re-resgistration.interface';
import { ConsolidatedPosition } from '@toro/common/shared/models/pos-risk/consolidated-position';

import { CheckoutWrapperComponent } from '../../../shared/panels/checkout/wrapper/checkout-wrapper.component';
import { ModalComponent } from '@toro/common/shared/components/modal/modal.component';
import { BlipSacEventsTrigger } from '@toro/common/shared/components/chat-blip/blip-sac-events-trigger';
import { DisposableSafelly } from '@toro/common/shared/components/disposable-safelly/disposable-safelly.component';
import { GuaranteeAsidePanelComponent } from '../../../shared/panels/guarantee-aside-panel/guarantee-aside-panel.component';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';
import { ActionEventsEnum } from '@toro/common/shared/enum/tracking/action-events.enum';

//#endregion
declare var $: any;

@AutoUnsubscribe()
@Component({
    selector: 'stockhome',
    styleUrls: ['./stockhome.component.scss'],
    templateUrl: './stockhome.component.html',
})
export class StockHomeComponent extends DisposableSafelly implements OnDestroy, OnInit {

    // Verifica se deve exibir o saldo do cliente ou o box de delay
    public get disponibleToInvest() {
        return (!this.isIdentified && !this.isRegistered && this.isLogged) ? true : false;
    }

    @ViewChild('headerPage', { static: true }) public headerPage: ElementRef;
    @ViewChild('tabbar', { static: true }) public tabbar: ElementRef;
    @ViewChild('modalUserRegister', { static: true }) public modalUserRegister: ModalComponent;

    public routerSubscription: Subscription;

    public marketTooltipTemplate: string;
    public offSetBanner: any;
    public registerAppUrl: string = this.settingsService.getConfigurationKeyValue('rau');
    public marketOpen: boolean = true;
    public isIdentified: boolean = null;
    public isLogged: boolean = null;
    public isRegistered: boolean = null;
    public activeTab: string;
    public closingTime: string;
    public openingTime: string;
    public infoproducts: any;
    public leverageLevel: LeverageLevel;

    public isLiveAttached: boolean = false;

    public totalOperationGuarantee: number;
    public accountBalance: number;

    public modalUserExpired: IReRegistrationMessage;
    public blipSacEventsTrigger: BlipSacEventsTrigger;

    public limitIncreased: boolean = false;
    private usingZeroModel: boolean = false;

    constructor(
        public router: Router,
        private store: Store<any>,
        private settingsService: SettingsService,
        private dialogsServices: DialogsService,
        private dateFormat: DateFormatService,
        private activeRouter: ActivatedRoute,
        private appcuesOnboardingObserver: AppcuesOnboardingObserver,
        public authEffects: UsersEffects,
        private userReRegistationService: UserReRegistationService,
        private utilitiesService: UtilitiesService
    ) {
        super();
        this.activeTab = this.router.url;
        
        // Executa todos os dispatchs
        this.callAllDispatches();
        this.onLoginStatusChanged();
    }

    public ngOnInit() {
        this.bindFinanceSubscribe();
        this.bindUserSubscribe();
        this.bindTimeSubscribe();
        this.bindInfoProducts();
        this.bindLiveSubscribe();

        this.openNewCheckoutSubscription();
        
        this.monitorRouterEvents();
        this.blipTrigger();
    }

    public monitorRouterEvents() {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            startWith(this.router)
        )
        .subscribe(() => {
            this.activeTab = this.router.url;

            this.openEquityAndGuarantee();
        });
    }

    public ngAfterContentInit() {
        this.offSetBanner = this.headerPage.nativeElement.offsetHeight;
        this.getUserRegistrationStore();
    }

    /**
     * Recebe informações financeiras consolidadas
     */
    public bindFinanceSubscribe() {
        this.disposableSubscriptions.push(
            this.store
            .select((financeStore) => financeStore.finance.consolidatePosition)
            .pipe(
                filter((consolidatePosition) => !!consolidatePosition),
            )
            .subscribe((consolidatePosition: ConsolidatedPosition) => {
                this.accountBalance = consolidatePosition.totalAvailableOnAccountD0;
                this.leverageLevel = consolidatePosition.leverageLevel
            }),
        );
    }

    /**
     * Recebe infoprodutos do usuário
     */
    public bindInfoProducts() {
        this.disposableSubscriptions.push(
            this.store
                .select((subscriptionStore) => subscriptionStore.digitalSubscription.purchases)
                .subscribe( (subscriptionData) => {
                    this.infoproducts = subscriptionData;
                }),
        );
    }

    /**
     * Recebe informações sobre o usuário logado
     */
    public bindUserSubscribe() {
        this.disposableSubscriptions.push(
            combineLatest([
                this.store.select((user) => user.userProfile),
                this.store.select((user) => user.authentication.currentUser),
            ])
            .pipe(
                filter(([userState, currentUser]) => !!userState && !!currentUser),
                take(1),
            )
            .subscribe(([userState, currentUser]) => {
                this.isLogged = true;
                this.isIdentified = currentUser.role === Roles.Identified;
                this.isRegistered = currentUser.role === Roles.Registered;
                this.limitIncreased = userState.riskModel === RiskModel.Two; // ?
                if (!this.isIdentified) {
                    this.store.dispatch({ type: StocksExchangeWalletAction.getInvestments });
                }
            }),
        );
    }

    /**
     * Busca informações da hora local e do mercado
     */
    public bindTimeSubscribe() {
        this.disposableSubscriptions.push(
            combineLatest([
                this.store.select((state) => state.exchange.tradingTime),
                this.store.select((state) => state.utils.timer),
            ])
            .pipe(
                filter(([tradingTime, time]) => !!tradingTime),
            )
            .subscribe(([tradingTime, time]) => {
                const now = time ? time : new Date().toLocaleTimeString("pt-BR", {timeZone: "America/Sao_Paulo"});
                this.openingTime = this.dateFormat.extractTimeFromDateTime(tradingTime.openingFuture as string);
                this.closingTime = this.dateFormat.extractTimeFromDateTime(tradingTime.closingFuture as string);

                if (now >= this.openingTime && now <= this.closingTime) {
                    this.marketOpen = true;
                } else {
                    this.marketOpen = false;
                    this.updateTooltipTemplate(
                        this.dateFormat.extractTimeFromDateTime(tradingTime.openingBovespa as string),
                        this.dateFormat.extractTimeFromDateTime(tradingTime.closingBovespa as string),
                        this.dateFormat.extractTimeFromDateTime(tradingTime.openingFuture as string),
                        this.dateFormat.extractTimeFromDateTime(tradingTime.closingFuture as string),
                    );
                }
            }),
        );
    }

    /**
     * Verifica status de janela da Live
     */
    public bindLiveSubscribe() {
        this.disposableSubscriptions.push(
            this.store
                .select((globalStore) => globalStore.globals.liveIsAttached)
                .subscribe( (isAttached) => {
                    this.isLiveAttached = isAttached;
                }),
        );
    }

    /**
     * Verifica se o usuário está em um nível avançado de alavancagem
     */
    public get isLeverageWarning() {
        return this.leverageLevel && this.leverageLevel === LeverageLevel.high;
    }

    /**
     * Verifica qual checkout deve ser exibido ao usuário
     */
    public openNewCheckoutSubscription(){
        this.disposableSubscriptions.push(
            combineLatest([
                this.store.select((state) => state.billing.brokerageModel),
                this.store.select((state) => state.exchangeCheckout.stockCheckout)
            ])
            .pipe(
                filter(([brokerageModel, stockCheckout]) => !!brokerageModel && !!stockCheckout),
            )
            .subscribe(([brokerageModel, stockCheckout]) => {
                if (brokerageModel && brokerageModel.type) {
                    this.usingZeroModel = (brokerageModel.type === BrokerageModel.Zero);
                    const queryParams = this.activeRouter.snapshot.queryParams;
                    if(queryParams && queryParams.checkout === 'novo-checkout'){
                        if (this.usingZeroModel && stockCheckout) {
                            this.openNewCheckout();
                        } else {
                            this.router.navigate([this.router.url.split('?')[0]], {queryParams: null})
                        }
                    }
                }
            }),
        )
    }

    @HostListener('window:scroll', ['$event']) public onScroll(event) {

        const scroll = $(window)
            .scrollTop();
        const tab = this.tabbar.nativeElement.offsetTop;

        if (scroll < tab) {
            document.getElementById('tabBar').classList
                .remove('tab-sticky');
        } else if (scroll >= tab) {
            document.getElementById('tabBar').classList
                .add('tab-sticky');
        }
    }

    public openNewCheckout() {
        this.dialogsServices.showComponentAsAsidePanel(CheckoutWrapperComponent, true);
    }

    public openEquityAndGuarantee() {
        if(this.router.url.includes('garantias-disponiveis')) {
            this.dialogsServices.showComponentAsAsidePanel(GuaranteeAsidePanelComponent, true);
        }
    }

    public callAllDispatches() {
        if (this.isLogged) {
            this.store.dispatch({ type: UserProfileActions.getRiskModel });
            this.appcuesOnboardingObserver.listen();
        }
        this.store.dispatch({ type: UtilsAction.getTimer });
        this.store.dispatch({ type: ExchangeActions.getTradingTime });
        this.store.dispatch({ type: StocksMarketAction.getMarketStatus });
        this.store.dispatch({ type: StocksExchangeWalletAction.getCustodyUpdates });
        this.store.dispatch({ type: StocksExchangeWalletAction.getExchangeUpdates });
        this.store.dispatch({ type: StocksMarketAction.getQuote, payload: KeyStocks.DolPtax });
        this.store.dispatch({ type: FinanceActions.getRealtimeConsolidatedPosition });
        this.store.dispatch({ type: FinanceActions.getRealtimeFinancePosition });
        this.store.dispatch({ type: ResearchActions.reasearchAdvicesSubscribe });
        this.store.dispatch({ type: SuitabilityBypassAgreementActions.getSuitabilityBypassAgreement});
    }

    // Starta o carrousel de videos
    public startCarousels() {
        if (!$('.learn-carousel--js').length) return;
        if (this.utilitiesService.isMobile()) {
            $('.learn-carousel--js')
                .slick({
                    infinite: false,
                    slidesToShow: 4,
                    dots: false,
                    responsive: [{
                        breakpoint: 767,
                        settings: {
                            slidesToShow: 1.1,
                            slidesToScroll: 1,
                            infinite: true,
                        },
                    }],
                });
        } else {
            $('.learn-carousel--js')
                .slick({
                    infinite: false,
                    slidesToShow: 4,
                    dots: false,
                    responsive: [{
                        breakpoint: 767,
                        settings: {
                            slidesToShow: 1,
                            slidesToScroll: 1,
                            infinite: true,
                        },
                    }],
                });
        }
    }

    // Atualiza o horario do tooltip para o horario recebido pelo tradingTime
    public updateTooltipTemplate(openingBovespa, closingBovespa, openingFuture, closingFuture) {
        this.marketTooltipTemplate = `<div>
            <div class='pop-title'>Mercado</div>
            <div class='pop-content'>
                A Bolsa de Valores funciona de ${openingBovespa} às ${closingBovespa} para a compra e venda de ações e de 
                ${openingFuture} às ${closingFuture} para índice e dólar.
            </div>
        </div>`;
    }

    // não remover, necessário para o funcionamento correto do @AutoUnsubscribe
    public ngOnDestroy() {
        this.dialogsServices.closeAll();
        this.disposableSubscriptions
            .forEach((subscription) => subscription.unsubscribe());
    }

    public blipTrigger() {
        this.blipSacEventsTrigger = new BlipSacEventsTrigger;
        this.blipSacEventsTrigger.verifyUrlAndFireEventIfNeedly();
    }

    private onLoginStatusChanged() {
        this.disposableSubscriptions.push(
            this.authEffects.forceAuthenticationRequest$
                .pipe(filter((action) => action.type.equals(UsersAction.forcedAuthenticationSuccefully)))
                .subscribe((action) => {
                    this.store.dispatch({ type: FinanceActions.getRealtimeConsolidatedPosition });
                    this.store.dispatch({ type: UserProfileActions.getRiskModel });
                }),
        );
    }

    /**
     * Busca os registros status do usuário
     */
    private getUserRegistrationStore() {
        this.store.select(({ userProfile }) => (userProfile))
            .pipe(take(2))
            .subscribe((up) => {
                if (!up.user || !+up.user.registrationStatus) { return; }
                this.verifyUserReRegister(+up.user.registrationStatus);
            });
    }

    /**
     * verifica se usuário esta expirado
     * Expired = 15
     */
    private verifyUserReRegister(registrationStatus: number) {
        const userExpired = this.userReRegistationService.validateUserExpired(registrationStatus);
        if (!!userExpired) {
            this.modalUserExpired = userExpired;
            this.modalUserRegister.show();
            return;
        }
    }

    public sendTracking(event: string, breakdowns: any) {
        this.store.dispatch({
            type: TrackingActions.trackEvents,
            payload: { type: event, events: breakdowns },
        });
    }

    public sendGuaranteeModalTracking() {
        this.sendTracking(ActionEventsEnum.ELEMENT_CLICKED, {
            'Product Area Origin': 'Bolsa',
            'Element': 'Link garantias disponíveis',
            'Step': this.activeTab == '/meus-investimentos' ? 'Meus investmentos' : 'Explore o mercado',
            'Time Responsável': 'Roebling',
        });
    }
}
